import { Flex } from '@brightdrop/bd-ui';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
} from '@mui/material';
import { FileUploader } from 'react-drag-drop-files';
import ChevronDownIcon from '~/assets/icons/new/chevron-down.svg?react';
import CloseIcon from '~/assets/icons/new/close.svg?react';
import ErrorIcon from '~/assets/icons/new/error.svg?react';
import ImageUploadIcon from '~/assets/icons/new/imageUpload.svg?react';
import TranslatedLabel, {
  translateText,
} from '~/common/components/TranslatedLabel/TranslatedLabel';
import useStyles from './FeedbackAttachment.styles';
import {
  FEEDBACK_IMAGE_TYPES,
  MAX_FEEDBACK_IMAGE_ATTACHMENTS,
} from './feedback.constants';
import type { FeedbackAttachmentItem } from './feedbackAttachment.types';
import {
  formatFeedbackImageMaxSizeLabel,
  getMaxFeedbackImageBytes,
} from './feedbackImageLimits';

/** Matches `getMaxFeedbackImageSizeMbForUploader` — derive from `maxBytes` to stay in sync and avoid a second env read. */
const BYTES_PER_MIB = 1024 * 1024;

export type FeedbackAttachmentProps = {
  attachmentItems: FeedbackAttachmentItem[];
  uploadSizeError: boolean;
  onFilesSelected: (selected: File | FileList | File[] | null) => void;
  onSizeError: () => void;
  onRemoveAttachment: (id: string) => void;
};

const FeedbackAttachment = ({
  attachmentItems,
  uploadSizeError,
  onFilesSelected,
  onSizeError,
  onRemoveAttachment,
}: FeedbackAttachmentProps) => {
  const { classes } = useStyles();

  return (
    <Accordion className={classes.accordion}>
      <AccordionSummary expandIcon={<ChevronDownIcon />}>
        <TranslatedLabel
          translationKey="feedback:cta.addScreenshot"
          typographyVariant="label_medium"
        />
      </AccordionSummary>
      <AccordionDetails>
        <FeedbackAttachmentDragAndDrop
          attachmentItems={attachmentItems}
          uploadSizeError={uploadSizeError}
          onFilesSelected={onFilesSelected}
          onSizeError={onSizeError}
          onRemoveAttachment={onRemoveAttachment}
        />
      </AccordionDetails>
    </Accordion>
  );
};

const FeedbackAttachmentDragAndDrop = ({
  attachmentItems,
  uploadSizeError,
  onFilesSelected,
  onSizeError,
  onRemoveAttachment,
}: FeedbackAttachmentProps) => {
  const { classes } = useStyles();

  const maxBytes = getMaxFeedbackImageBytes();
  const maxSizeLabel = formatFeedbackImageMaxSizeLabel(maxBytes);
  const maxSizeMb = maxBytes / BYTES_PER_MIB;

  const canAddMore = attachmentItems.length < MAX_FEEDBACK_IMAGE_ATTACHMENTS;

  const fileUploaderProps = {
    handleChange: onFilesSelected,
    onSizeError: onSizeError,
    name: 'feedback-screenshot',
    types: FEEDBACK_IMAGE_TYPES,
    multiple: false,
    maxSize: maxSizeMb,
  } as const;

  return (
    <Flex col>
      <Box className={classes.container}>
        {attachmentItems.length > 0 ? (
          <Box className={classes.previewSection}>
            <Box className={classes.previewRow}>
              {attachmentItems.map((item) => (
                <Box key={item.id} className={classes.previewItemContainer}>
                  <Box className={classes.previewThumb}>
                    <img
                      className={classes.previewImg}
                      src={item.previewUrl}
                      alt={translateText('feedback:cta.attachmentPreview')}
                    />
                  </Box>
                  <button
                    type="button"
                    aria-label={`${translateText('feedback:cta.removeAttachment')} ${item.file.name}`}
                    className={classes.removeButton}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      onRemoveAttachment(item.id);
                    }}
                  >
                    <CloseIcon className={classes.removeIcon} />
                  </button>
                </Box>
              ))}
              {canAddMore && (
                <FileUploader {...fileUploaderProps}>
                  <Flex className={classes.dragAndDrop} center>
                    <ImageUploadIcon />
                  </Flex>
                </FileUploader>
              )}
            </Box>
          </Box>
        ) : (
          <FileUploader {...fileUploaderProps}>
            <Flex col center className={classes.uploadSection}>
              <ImageUploadIcon className={classes.imageUploadIcon} />
              <TranslatedLabel
                translationKey="feedback:cta.upload"
                typographyVariant="caption_regular"
              />
              <TranslatedLabel
                translationKey="feedback:cta.extensions"
                typographyVariant="caption_regular"
                variables={{ maxSize: maxSizeLabel }}
              />
            </Flex>
          </FileUploader>
        )}
      </Box>
      {uploadSizeError && (
        <Box
          className={classes.sizeErrorRow}
          role="alert"
          data-testid="feedback-attachment-size-error"
        >
          <ErrorIcon className={classes.sizeErrorIcon} aria-hidden />
          <TranslatedLabel
            translationKey="feedback:cta.uploadSizeError"
            typographyVariant="caption_regular"
            className={classes.sizeErrorText}
            variables={{ maxSize: maxSizeLabel }}
          />
        </Box>
      )}
    </Flex>
  );
};

export default FeedbackAttachment;
